import React, { useEffect, useState } from 'react';
import integrationsService from '../../services/integrationsService';
import { apiErrorMessage } from '../../utils/apiError';
import TestRunLogLink from './TestRunLogLink';
import { formatDateTime } from '../../utils/datetime';
import './AddRouteModal.css';
import './OxiTestModal.css';

// TEST AN INTEGRATION SOURCE'S OXI ENDPOINT — opened from the Test button on the designer's System box.
//
// Two things OPERA's OXI does, and both can be tried from here:
//   Push  POST /oxi/{key} with a message — what OXI does when something changes in OPERA
//   Poll  GET  /oxi/{key}                — what OXI does to collect what is waiting for it
//
// The call is made by the server with the source's stored key, through the public Integrations API
// (CloudFront + WAF), so what comes back is what OXI itself would get. The key never reaches the browser.

const MESSAGE_TYPES = ['RESERVATION', 'PROFILE', 'ALLOTMENT', 'CATEVENT', 'PACKAGES'];

const newTransactionId = () => `HCONN-TEST-${Date.now()}`;

// THE PALETTE — one real OXI message per variant, taken from production message logs and anonymised
// (oxiTestPalette.json): sync project 350 for OPERA 5 (mfReservationAction ADD/EDIT/SYNCH…) and 323 for OPERA
// Cloud (NEW RESERVATION/UPDATE RESERVATION/RESYNC…, catering events, packages). Every name, address,
// contact, comment, staff user code, card/membership/document number and id is replaced with a test value;
// the structure is exactly what OXI sends. {{PROPERTY}} and {{TRANSACTION}} are filled in here.
//
// Loaded when the modal opens rather than with the page: 31 full messages are ~230 KB nobody else needs.
const fill = (xml, property, txn, extra = {}) => Object.entries({
    PROPERTY: property || 'PROPERTY',
    TRANSACTION: txn,
    TIMESTAMP: new Date().toISOString(),
    ...extra,
}).reduce((out, [k, v]) => out.split(`{{${k}}}`).join(v == null ? '' : v), xml);

// ── ONE MODAL, ONE SUITE PER SOURCE ─────────────────────────────────────────
//
// Pick a real message, edit it, send it, read what came back. That is the same question for OPERA OXI
// and for Pegasus, and the answer differs only in what a message LOOKS like and where it is posted —
// so this is a table, not a second modal. (The MCP suite is genuinely different: there is no message to
// pick, only a tool to call, which is why McpTestModal exists at all.)
//
// A SUITE SAYS WHAT IS TRUE OF THAT WIRE, and nothing else in here branches on the system.
const SUITES = {
    oxi: {
        palette: () => import('./oxiTestPalette.json'),
        // OXI is both pushed to and polled from; Pegasus only ever pushes (marketplace_to_hub =
        // HTTP_PUSH, hub_to_marketplace = NONE), so offering Poll would offer a thing that cannot work.
        poll: true,
        pushHint: 'POST — as OXI sends a change',
        pollHint: 'GET — as OXI collects what is waiting',
        bodyLabel: 'XML',
        // The step this wire's proxy logs an ARRIVING message under — how "what has this source
        // actually received" is found. Named per suite because each proxy names its own steps.
        inboundStep: 'oxi.push.received',
        // OXI carries the type, property and transaction on the QUERY STRING, so they are fields.
        envelopeCarriesRouting: false,
        send: (svc, id, body) => svc.testOxi(id, body),
        hint: () => (
            <>Sent as <code>text/xml</code> with the type, property and transaction on the query
            string, the way OXI sends them.</>
        ),
    },
    // THE ONE SOURCE THAT IS NOT TESTED THE WAY IT IS USED, and the suite says so rather than hiding it.
    // Opera Cloud does not push: the hub polls Oracle's business event stream and fetches the record for
    // each event. There is no arriving message to imitate, and no way to make Oracle have an event
    // waiting on demand — so the test starts one step later, with the record already in hand, and hands
    // it to the proxy exactly as the poller would. What that proves is what the integration does with a
    // record. What it cannot prove is that the poll itself is working.
    ohip: {
        palette: () => import('./ohipTestPalette.json'),
        // Nothing collects from us on this wire — Opera Cloud is polled BY us — so there is no Poll.
        poll: false,
        inboundStep: 'ohip.record.received',
        // THE `meta` ENVELOPE CARRIES EVERYTHING — messageType is the module, actionType is what
        // happened, property is the resort and transactionId is Oracle's own event id. So there is no
        // query string to fill in, and the fields that would edit one are not shown.
        envelopeCarriesRouting: true,
        // WHAT THE `meta` ENVELOPE CARRIES, and only what it carries. The credential fields the Pegasus
        // envelope shows have no counterpart here: the caller is the hub's own poller, reached through
        // the same front door as every other route, so the key in the path is the whole of its identity.
        envelope: [
            { key: 'property', label: 'Property (meta.property)', placeholder: 'e.g. HCONN1' },
            { key: 'transactionId', label: "Oracle's action id (meta.transactionId)", mono: true, grow: true },
        ],
        pushHint: "POST — as the hub's poller hands over a record it has just fetched",
        // JSON, NOT XML. OHIP is a JSON API and the samples are OHIP's own documents; labelling the box
        // XML would be the one thing on this screen telling a reader the wrong thing about the wire.
        bodyLabel: 'JSON',
        send: (svc, id, body) => svc.testOhip(id, body),
        hint: () => (
            <>Sent as <code>application/json</code> to <code>/ohip/&#123;key&#125;</code>, the way the
            hub's poller hands over a record it has just fetched. The module, the action and the property
            are all in the <code>meta</code> envelope.</>
        ),
    },
    pegasus: {
        palette: () => import('./pegasusTestPalette.json'),
        poll: false,
        inboundStep: 'pegasus.push.received',
        // A PEGASUS ENVELOPE CARRIES EVERYTHING — the OTA element is the message type, HotelCode is the
        // property, EchoToken is the transaction, and the HTNG header holds the credentials. So there
        // is no query string to fill in, and the fields that would edit one are not shown: they would
        // be four boxes that change nothing.
        envelopeCarriesRouting: true,
        // The header credentials ARE part of the test — the proxy checks them against the source's
        // credential, so sending the wrong ones is a case worth being able to try.
        credentials: true,
        envelope: [
            { key: 'property', label: 'HotelCode', placeholder: 'e.g. AUHRO' },
            { key: 'username', label: 'Header username', placeholder: 'INBOUNDUSERNAME' },
            { key: 'password', label: 'Header password', placeholder: 'INBOUNDPASSWORD', type: 'password' },
            { key: 'transactionId', label: 'EchoToken', mono: true, grow: true },
        ],
        pushHint: 'POST — as Pegasus sends a change',
        bodyLabel: 'XML',
        send: (svc, id, body) => svc.testPegasus(id, body),
        hint: () => (
            <>Sent as <code>text/xml</code> to <code>/pegasus/&#123;key&#125;</code>. The message type,
            the property and the credentials are all inside the envelope — there is no query string.</>
        ),
    },
};

// TESTED AS ONE PROPERTY (V508). `implementation` is the row the button was pressed on — its property
// code seeds the message and the server records the test against that property, instead of against
// whatever hotel the template was created for.
const OxiTestModal = ({ isOpen, onClose, integrationId, source, implementation, suite = 'oxi' }) => {
    const spec = SUITES[suite] || SUITES.oxi;
    const [direction, setDirection] = useState('push');
    // The HTNG header credentials, for a suite that authenticates in the message.
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [palette, setPalette] = useState([]);
    const [sampleId, setSampleId] = useState('');
    const [messageType, setMessageType] = useState('RESERVATION');
    const [propertyName, setPropertyName] = useState('');
    const [transactionId, setTransactionId] = useState(newTransactionId());
    const [message, setMessage] = useState('');
    const [edited, setEdited] = useState(false);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    // ── WHAT THIS SOURCE HAS ACTUALLY RECEIVED ──────────────────────────────
    //
    // The built-in samples are invented specimens: right in shape, and the wrong thing entirely when
    // the question is "why did THIS message fail" — which is why somebody opens this modal. So the
    // picker also offers real traffic, newest first, already redacted by the proxy that logged it.
    //
    // FETCHED ALONGSIDE, NOT INSTEAD. A source that has never received anything still needs the
    // specimens, and a log that has aged out (14 days) must not leave the modal empty.
    const [recent, setRecent] = useState([]);
    const [recentError, setRecentError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (!isOpen || !integrationId || !source || !source.id) return undefined;
        setRecent([]); setRecentError(null);
        integrationsService.testMessages(integrationId, { nodeId: source.id, step: spec.inboundStep })
            .then(res => {
                if (cancelled) return;
                setRecent(res.messages || []);
                if (res.error) setRecentError(res.error);
            })
            .catch(e => { if (!cancelled) setRecentError(apiErrorMessage(e, 'Recent messages could not be read')); });
        return () => { cancelled = true; };
    }, [isOpen, integrationId, source, spec]);

    // One list for the picker: real messages first, because they are the reason to open it.
    const allSamples = [
        ...recent.map((m, i) => ({
            id: `recent-${m.transaction_id || i}`,
            label: `${m.message_type || 'message'}${m.property ? ` · ${m.property}` : ''}`
                + ` · ${formatDateTime(m['@timestamp'])}`,
            messageType: m.message_type || 'RESERVATION',
            flavour: 'received by this source',
            detail: m.transaction_id || '',
            xml: m.payload,
            isReal: true,
        })),
        ...palette,
    ];

    useEffect(() => {
        if (!isOpen || palette.length) return;
        spec.palette()
            .then(m => setPalette(m.default || m))
            .catch(() => setPalette([]));
        // Keyed on the suite as well: switching source without this would show OXI's messages on a
        // Pegasus board, and they would be sent.
    }, [isOpen, palette.length, spec]);

    // A suite change empties the palette, so the next open loads that suite's messages.
    useEffect(() => { setPalette([]); setSampleId(''); }, [suite]);

    // Fresh on every open: a modal that remembers the last test's transaction id would send a duplicate,
    // which the queue collapses and the test then appears to have done nothing.
    useEffect(() => {
        if (!isOpen) return;
        setDirection('push');
        // THE IMPLEMENTATION'S PROPERTY CODE FIRST. It is the one this test is being sent as; the
        // node's override is the template's leftover and is only right when there is no implementation.
        setPropertyName((implementation && implementation.hotelid_override)
            || (source && source.hotelid_override) || '');
        setTransactionId(newTransactionId());
        setSampleId('');
        setEdited(false);
        setResult(null);
        setError(null);
    }, [isOpen, source, implementation]);

    // Default to the first message once the palette has loaded.
    useEffect(() => {
        // A REAL MESSAGE IF THERE IS ONE, otherwise the first specimen. Opening on real traffic is the
        // whole point of fetching it — defaulting to a specimen would leave it one click away and
        // unnoticed.
        if (isOpen && allSamples.length && !sampleId) setSampleId(allSamples[0].id);
    }, [isOpen, allSamples, sampleId]);

    // WHAT A SUITE'S `envelope` ENTRY ADDRESSES. Every envelope field is one of these four pieces of
    // state — which is why a suite only has to name the ones its wire actually carries, and in what
    // words. A key with no entry here renders a dead box rather than crashing.
    const envelopeFields = {
        property: [propertyName, setPropertyName],
        transactionId: [transactionId, setTransactionId],
        username: [username, setUsername],
        password: [password, setPassword],
    };

    const chosen = allSamples.find(p => p.id === sampleId) || null;

    // Picking a message sets its type; the text follows the chosen message, property and transaction until
    // the author edits it by hand.
    useEffect(() => {
        if (chosen) setMessageType(chosen.messageType);
    }, [chosen]);
    useEffect(() => {
        if (isOpen && chosen && !edited) {
            setMessage(fill(chosen.xml, propertyName, transactionId,
                { USERNAME: username, PASSWORD: password }));
        }
    }, [chosen, propertyName, transactionId, username, password, edited, isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape' && !sending) onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose, sending]);

    if (!isOpen) return null;

    // yyyy-mm-dd hh:mm:ss.mmm + zone — milliseconds, so it lines up with the clock on the Logs tab.
    const stamp = (iso) => {
        if (!iso) return '—';
        const ms = String(new Date(iso).getMilliseconds()).padStart(3, '0');
        return formatDateTime(iso).replace(/(\d{2}:\d{2}:\d{2})/, `$1.${ms}`);
    };

    const send = async () => {
        setSending(true);
        setError(null);
        setResult(null);
        try {
            const res = await spec.send(integrationsService, integrationId, {
                nodeId: source.id,
                implementationId: implementation && implementation.id,
                direction,
                messageType,
                propertyName,
                transactionId,
                message,
            });
            setResult(res);
            // A new transaction for the next push, or OXI's own de-duplication swallows it.
            if (direction === 'push') setTransactionId(newTransactionId());
        } catch (e) {
            setError(apiErrorMessage(e, 'The test could not be sent.'));
        } finally {
            setSending(false);
        }
    };

    const status = result && result.response ? result.response.status : null;
    const ok = status !== null && status >= 200 && status < 300;

    return (
        <div className="modal-overlay" onClick={() => !sending && onClose()}>
            <div className="modal-content modal-md acm-modal otm" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="otm-title">
                <div className="modal-header">
                    <h2 id="otm-title">
                        Test {source ? (source.adapter || source.system_name) : 'source'}
                        {implementation && <span className="form-help">
                            {' '}as {implementation.hotelname || implementation.name}
                        </span>}
                    </h2>
                    <button className="modal-close" onClick={onClose} disabled={sending} aria-label="Close">×</button>
                </div>

                <div className="modal-body">
                    <div className="otm-direction" role="radiogroup" aria-label="What to test">
                        <button type="button" role="radio" aria-checked={direction === 'push'}
                            className={direction === 'push' ? 'is-on' : ''} onClick={() => setDirection('push')}>
                            Push a message
                            <small>{spec.pushHint || 'POST — as the system sends a change'}</small>
                        </button>
                        {spec.poll && (
                            <button type="button" role="radio" aria-checked={direction === 'poll'}
                                className={direction === 'poll' ? 'is-on' : ''} onClick={() => setDirection('poll')}>
                                Poll for next message
                                <small>{spec.pollHint || 'GET — as the system collects what is waiting'}</small>
                            </button>
                        )}
                    </div>

                    {direction === 'push' && (
                        <>
                            <div className="acm-field">
                                <label className="acm-label" htmlFor="otm-sample">Message</label>
                                <select
                                    id="otm-sample"
                                    className="acm-select"
                                    value={sampleId}
                                    onChange={e => { setSampleId(e.target.value); setEdited(false); }}
                                    disabled={!allSamples.length}
                                >
                                    {!allSamples.length && <option value="">Loading messages…</option>}
                                    {[...new Set(allSamples.map(p => p.flavour))].map(flavour => (
                                        <optgroup key={flavour} label={flavour}>
                                            {allSamples.filter(p => p.flavour === flavour).map(p => (
                                                <option key={p.id} value={p.id}>{p.label}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                                {chosen && (
                                    <p className="acm-hint">
                                        {chosen.detail && <code>{chosen.detail}</code>}
                                        {chosen.isReal
                                            ? ' · a message this source really received, as it was logged'
                                            // BUILT FROM THE SPECIFICATION, not captured: no real message of
                                            // that type has been logged yet, and saying "anonymised" would
                                            // claim a provenance it does not have.
                                            : chosen.origin === 'spec'
                                                ? " · built from Oracle's OHIP specification — no real message of this type has been captured yet"
                                                : ` · from a real ${chosen.flavour} message, anonymised`}
                                    </p>
                                )}
                                {/* SAID, NOT SWALLOWED. "No recent messages" and "the log could not be
                                    read" look identical in a dropdown and mean different things. */}
                                {recentError && <p className="acm-hint">Recent messages: {recentError}</p>}
                                {!recentError && !recent.length && (
                                    <p className="acm-hint">
                                        This source has received nothing in the last 14 days, so only the
                                        built-in samples are offered.
                                    </p>
                                )}
                            </div>
                            {!spec.envelopeCarriesRouting ? (
                                <div className="otm-row">
                                    <div className="acm-field">
                                        <label className="acm-label" htmlFor="otm-type">Message type</label>
                                        <select id="otm-type" className="acm-select" value={messageType} onChange={e => setMessageType(e.target.value)}>
                                            {MESSAGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div className="acm-field">
                                        <label className="acm-label" htmlFor="otm-property">Property code</label>
                                        <input id="otm-property" className="acm-select" value={propertyName} onChange={e => setPropertyName(e.target.value)} placeholder="e.g. JENA" />
                                    </div>
                                    <div className="acm-field otm-grow">
                                        <label className="acm-label" htmlFor="otm-txn">Transaction id</label>
                                        <input id="otm-txn" className="acm-select otm-mono" value={transactionId} onChange={e => setTransactionId(e.target.value)} />
                                    </div>
                                </div>
                            ) : (
                                /* WHAT GOES INTO THE ENVELOPE. These are not query parameters — they are
                                   written into the message below, which is what the proxy then reads.
                                   
                                   DECLARED BY THE SUITE, NOT WRITTEN HERE. This used to be Pegasus's four
                                   boxes hardcoded behind a boolean, so the OHIP suite — which sets the
                                   same boolean because its routing is also in the body — rendered
                                   HotelCode, EchoToken and a pair of header credentials that its wire
                                   does not have and its proxy never reads. One flag cannot describe two
                                   different envelopes; the fields are data on the suite instead. */
                                <div className="otm-row">
                                    {(spec.envelope || []).map(f => {
                                        const [value, set] = envelopeFields[f.key] || ['', () => {}];
                                        return (
                                            <div key={f.key} className={`acm-field${f.grow ? ' otm-grow' : ''}`}>
                                                <label className="acm-label" htmlFor={`otm-${f.key}`}>{f.label}</label>
                                                <input
                                                    id={`otm-${f.key}`}
                                                    type={f.type || 'text'}
                                                    className={`acm-select${f.mono ? ' otm-mono' : ''}`}
                                                    value={value}
                                                    onChange={e => set(e.target.value)}
                                                    placeholder={f.placeholder || ''}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="acm-field">
                                <div className="otm-msg-head">
                                    <label className="acm-label" htmlFor="otm-message">{spec.bodyLabel || 'XML'}</label>
                                    {edited && (
                                        <button type="button" className="otm-link" onClick={() => setEdited(false)}>Reset to the chosen message</button>
                                    )}
                                </div>
                                <textarea
                                    id="otm-message"
                                    className="otm-message"
                                    value={message}
                                    onChange={e => { setMessage(e.target.value); setEdited(true); }}
                                    spellCheck={false}
                                    wrap="off"
                                    rows={14}
                                />
                                <p className="acm-hint">{spec.hint()}</p>
                            </div>
                        </>
                    )}

                    {direction === 'poll' && (
                        <p className="acm-hint">
                            Collects the next message waiting for this property, exactly as OXI would. <b>It is taken off the queue</b> —
                            a message collected here will not be delivered to OPERA.
                        </p>
                    )}

                    {error && <div className="otm-result is-error" role="alert">{error}</div>}

                    {result && (
                        <div className={`otm-result ${result.error ? 'is-error' : ok ? 'is-ok' : 'is-warn'}`}>
                            <div className="otm-result-head">
                                <span className="otm-status">{result.error ? 'Not reached' : status}</span>
                                <span className="otm-mono otm-url">{result.request.method} {result.request.url}</span>
                                <span className="otm-time">{result.durationMs} ms</span>
                            </div>
                            <div className="otm-stamps">
                                <span>Request sent <strong>{stamp(result.sentAt)}</strong></span>
                                <span>{result.error ? 'Gave up' : 'Response returned'} <strong>{stamp(result.answeredAt)}</strong></span>
                            </div>
                            {result.error && <p>{result.error}</p>}
                            {result.response && (
                                <pre className="otm-body">
                                    {result.response.body
                                        || (direction === 'poll' && ok ? '(empty — nothing is waiting for this property)' : '(empty body)')}
                                    {result.response.truncated ? '\n… (truncated)' : ''}
                                </pre>
                            )}
                            {/* WHAT THE PIPELINE THEN DID WITH IT. The answer above is what the
                                Integrations API said at the door; every step after it is on the Logs
                                tab, tagged with this run's transaction id. Shown on a failure too —
                                that is when it is read most. */}
                            <div className="otm-loglink">
                                <TestRunLogLink
                                    integrationId={integrationId}
                                    transactionId={result.transactionId || transactionId}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-footer otm-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose} disabled={sending}>Close</button>
                    <button type="button" className="btn btn-primary" onClick={send} disabled={sending || (direction === 'push' && !message.trim())}>
                        {sending ? 'Sending…' : direction === 'push' ? 'Send message' : 'Poll now'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OxiTestModal;
